if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 768px)');
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', () => {
          this.setActiveMedia(mediaToSwitch.dataset.target, false);
          this.selectVariantForThumbnail(mediaToSwitch);
        });
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }

    connectedCallback() {
      if (!this.elements.thumbnails) return;
      const scope = this.variantScope;
      if (!scope) return;

      // Delegated because <variant-selects> is replaced after every variant change.
      this.variantAbortController = new AbortController();
      scope.addEventListener(
        'change',
        (event) => {
          const variantSelects = event.target.closest('variant-selects');
          if (variantSelects) this.showMediaForSelectedOptions(variantSelects);
        },
        { signal: this.variantAbortController.signal }
      );
    }

    disconnectedCallback() {
      this.variantAbortController?.abort();
    }

    get variantScope() {
      return this.closest('product-info') || this.closest('.shopify-section');
    }

    parseVariantOptions(thumbnail) {
      try {
        return JSON.parse(thumbnail.dataset.variantOptions || '[]');
      } catch (error) {
        return [];
      }
    }

    // Picking a Color/Version value shows the thumbnail that stands for it (variant image or same position).
    showMediaForSelectedOptions(variantSelects) {
      const selected = new Map();
      variantSelects.querySelectorAll('input[type="radio"]:checked').forEach((input) => selected.set(input.name, input.value));
      variantSelects.querySelectorAll('select').forEach((select) => {
        const match = /^options\[(.*)\]$/.exec(select.name);
        if (match) selected.set(match[1], select.value);
      });

      const thumbnail = Array.from(this.elements.thumbnails.querySelectorAll('[data-variant-options]')).find((item) => {
        const options = this.parseVariantOptions(item);
        return options.length && options.every(({ name, value }) => selected.get(name) === value);
      });
      if (!thumbnail || thumbnail.querySelector('button')?.getAttribute('aria-current') === 'true') return;

      this.setActiveMedia(thumbnail.dataset.target, false);
    }

    onSlideChanged(event) {
      if (event.detail.currentElement) {
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
        this.setActiveThumbnail(thumbnail);
      }
    }
    
    setActiveMedia(mediaId, prepend) {
      const activeMedia =
      this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
      this.elements.viewer.querySelector('[data-media-id]');
      if (!activeMedia) {
        return;
      }
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia?.classList?.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        // Thumbnails keep their order: they select variants, so moving the active one to the front
        // would put a different image under the shopper's cursor after every change.
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail?.dataset.mediaPosition);
    }

    // Thumbnails carry the Color/Version values they stand for (snippets/media-variant-options).
    // Selecting them goes through <variant-selects>, so price, stock, URL and add to cart update as usual.
    selectVariantForThumbnail(thumbnail) {
      const options = this.parseVariantOptions(thumbnail);
      if (!options.length) return;

      const variantSelects = this.variantScope?.querySelector('variant-selects');
      if (!variantSelects) return;

      let changedInput = null;
      options.forEach(({ name, value }) => {
        const radio = Array.from(variantSelects.querySelectorAll('input[type="radio"]')).find(
          (input) => input.name === name && input.value === value
        );
        if (radio) {
          if (!radio.checked) {
            radio.checked = true;
            changedInput = radio;
          }
          return;
        }

        const select = Array.from(variantSelects.querySelectorAll('select')).find(
          (element) => element.name === `options[${name}]`
        );
        if (select && select.value !== value && Array.from(select.options).some((option) => option.value === value)) {
          select.value = value;
          changedInput = select;
        }
      });

      if (changedInput) changedInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
